"use client";

import { getTypedChildren } from "@faire/slate/lib/getTypedChildren";
import { componentGroupCls } from "@faire/slate/lib/tracking";
import { TabList } from "@faire/slate/private/tab/TabList";
import {
  ITabGroupWebProps,
  TabNavitaionMode,
} from "@faire/slate/private/tab/types";
import { useTabNavigation } from "@faire/slate/private/tab/useTabNavigation";
import { isNullish } from "@faire/typescript";
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Tab } from "./Tab";
import { getTabPanelId } from "./utils";

type IProps<T extends string> = {
  /**
   * **web**
   * The navigation mode (see https://www.w3.org/WAI/ARIA/apg/patterns/tabs).
   *
   * - `automatic` - User can switch tabs just by focusing on them or clicking on them.
   * - `manual` - User can switch tabs only by pressing Enter or Space or clicking on them.
   *
   * @default "manual"
   */
  mode?: TabNavitaionMode;

  /**
   * **web**
   * The selected tab value.
   */
  selectedTabValue?: T;

  /**
   * **web**
   * The tab group onSelect callback.
   *
   * @param value - The selected tab value.
   */
  onSelect?: (value: T) => void;
} & ITabGroupWebProps;

/**
 * A group of tabs.
 *
 * Tabs organize content across different views or data sets.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Tab-2242efb5c25a801ca542d716289431df | Slate/TabGroup}
 */
export const TabGroup = <T extends string = string>({
  children,
  mode = "manual",
  ref,
  selectedTabValue,
  onSelect,
  ...props
}: IProps<T>) => {
  const [{ tabs }, rest] = useMemo(
    () => getTypedChildren(children, { tabs: Tab<T> }),
    [children]
  );
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<Record<string, HTMLElement | null>>({});

  const tabsIds = useMemo(() => tabs.map((tab) => tab.props.value), [tabs]);
  const selected = tabsIds.find((id) => id === selectedTabValue);
  const [focused, setFocused] = useState<T>();

  if (rest.filter((v) => !isNullish(v)).length > 0) {
    // oxlint-disable-next-line no-console
    console.error("TabGroup accepts only Tab");
  }

  const handleFocus = useCallback((item: T | undefined) => {
    if (item) {
      setFocused(item);
      tabsRef.current[item]?.focus();
    }
  }, []);

  const handleSelect = useCallback(
    (item: T | undefined) => {
      if (item) {
        onSelect?.(item);
        handleFocus(item);
      }
    },
    [handleFocus, onSelect]
  );

  const { keyboardHandler } = useTabNavigation({
    items: tabsIds,
    mode,
    focused,
    selected,
    onFocus: handleFocus,
    onSelect: handleSelect,
  });

  useImperativeHandle(ref, () => tabListRef.current!);

  return (
    <TabList
      {...props}
      className={componentGroupCls()}
      data-component-name="Slate/TabGroup"
      ref={tabListRef}
      onKeyDown={keyboardHandler}
    >
      {tabs.map((tab, index) =>
        React.cloneElement(tab, {
          key: tab.props.value,
          id: tab.props.value,
          "aria-controls": getTabPanelId(tab.props.value),
          // Each tab is nested in its own wrapper element rather than being a
          // direct sibling of the other tabs, so screen readers cannot infer the
          // set size on their own (they announce "1 of 1" for every tab). Set the
          // position and size explicitly per the ARIA tabs pattern.
          "aria-setsize": tabs.length,
          "aria-posinset": index + 1,
          selected: selectedTabValue === tab.props.value,
          onSelect: handleSelect,
          ref: (el: HTMLElement | null) => {
            tabsRef.current[tab.props.value] = el;
          },
        })
      )}
    </TabList>
  );
};
